import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Body2, InfoIcon, Input, Tooltip } from "@activecollab/components";
import classNames from "classnames";
import { Field, useFormikContext } from "formik";
import { List } from "immutable";
import { capitalize } from "lodash";
import PropTypes from "prop-types";
import AngDropFiles from "~/components/component.ang_drop_files";
import { CustomFormField } from "~/components/CustomFormField";
import { useFileHandler } from "~/components/Hooks";
import InsertTextVariables from "~/components/InsertTextVariables/InsertTextVariables";
import { FormCheckbox } from "~/components/ModalObjects";
import { SelectAttachments } from "~/components/select_attachments/SelectAttachments";
import { SubscribersAvatarList } from "~/components/SubscribersAvatarList/SubscribersAvatarList";
import { SubscribersPicker } from "~/components/tasks/TaskPanel/subscribers/SubscribersPicker";
import VisualEditorContainer from "~/components/VisualEditor/VisualEditorContainer";
import { getPossibleSubscribers } from "~/store/selectors/selector.subscription";
import { keyCodes } from "~/utils/key_codes";
import { useProjectTemplateContext } from "./context";
import {
  PROJECT_TEMPLATE_DISCUSSION,
  PROJECT_TEMPLATE_NOTE,
  PROJECT_TEMPLATE_RECURRING_TASK,
  resolvedLabel,
} from "./utils";

const TemplateElementContent = ({ fieldRef, fileInputRef, additionalFields }) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const { values, setFieldValue } = useFormikContext();
  const { uploading, onUpload, ...fileHandlerRest } = useFileHandler();
  const { template } = useProjectTemplateContext();

  const [variableMenuOpen, setVariableMenuOpen] = useState(false);
  const [inputCursorPosition, setInputCursorPosition] = useState(values?.name?.length);
  const elementType = useMemo(() => values?.class || values?.type, [values?.class, values?.type]);
  const isDiscussionOrNote = useMemo(
    () => elementType === PROJECT_TEMPLATE_DISCUSSION || elementType === PROJECT_TEMPLATE_NOTE,
    [elementType]
  );

  const placeHolderText = useMemo(() => resolvedLabel(elementType, true), [elementType]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
    }
  }, []);

  const handleOpenVariableMenu = useCallback(() => {
    setVariableMenuOpen(!variableMenuOpen);
  }, [variableMenuOpen]);

  const handleCloseVariableMenu = useCallback(() => {
    setVariableMenuOpen(false);
  }, []);

  const handleAddVariable = useCallback(
    (name) => {
      const input = inputRef.current;

      if (input) {
        const currentValue = input.value;
        const variableText = name;
        const newPosition = inputCursorPosition + variableText.length;

        const updatedInput =
          currentValue.substring(0, inputCursorPosition) + variableText + currentValue.substring(inputCursorPosition);
        setVariableMenuOpen(false);
        setInputCursorPosition(newPosition);
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(newPosition, newPosition);
        }, 100);
        return updatedInput;
      }
    },
    [inputCursorPosition]
  );

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef?.current?.click();
  }, [fileInputRef]);

  const handleUpload = useCallback(
    (files) => {
      for (let i = 0; i < files.length; i++) {
        onUpload(files[i]);
      }
    },
    [onUpload]
  );

  const onSelectionChange = useCallback((e) => {
    setInputCursorPosition(e.target.selectionStart);
  }, []);

  const possibleSubscribers = useSelector((state) =>
    getPossibleSubscribers(state, template?.get(`members`)?.toJS() ?? [])
  );

  const clientIds = useMemo(
    () => (possibleSubscribers ? possibleSubscribers.filter((u) => u.class === "Client").map((u) => u.id) : []),
    [possibleSubscribers]
  );

  const subscriberIds = useMemo(() => {
    return possibleSubscribers
      ? possibleSubscribers
          .filter((u) => {
            if (values?.is_hidden_from_clients) {
              return u.class !== "Client";
            }
            return true;
          })
          .map((u) => u.id)
      : [];
  }, [values?.is_hidden_from_clients, possibleSubscribers]);

  useUpdateEffect(() => {
    if (!values?.is_hidden_from_clients) return;
    if (!possibleSubscribers || !values?.subscribers) return;

    const cleanedSubscribers = values.subscribers.filter((id) => !clientIds.includes(id));

    if (cleanedSubscribers.length !== values.subscribers.length) {
      setFieldValue("subscribers", cleanedSubscribers);
    }

    if (values?.assignee_id && clientIds.includes(values.assignee_id)) {
      setFieldValue("assignee_id", 0);
    }
  }, [values?.is_hidden_from_clients]);

  return (
    <>
      <div
        className={classNames("tw-flex-1 tw-rounded-md tw-bg-page-paper-main tw-flex tw-flex-col", {
          "tw-max-w-[660px] tw-h-full": !isDiscussionOrNote && elementType !== PROJECT_TEMPLATE_RECURRING_TASK,
          "tw-w-[588px]": elementType === PROJECT_TEMPLATE_RECURRING_TASK,
        })}
      >
        {additionalFields}
        <Field name="name">
          {({ form, field, meta }) => (
            <div className="tw-flex tw-flex-row tw-px-6 tw-py-1.5 tw-w-full tw-border-border-primary tw-border-solid tw-border tw-rounded-t-md tw-border-b-0">
              <Input
                ref={(el) => {
                  inputRef.current = el;
                  if (fieldRef) return (fieldRef.current.name = el);
                }}
                endAdornment={
                  <>
                    {meta.error && meta.touched ? (
                      <Tooltip title={capitalize(meta.error)}>
                        <InfoIcon
                          fill="var(--red-alert)"
                          className="tw-cursor-help tw-mt-0.5 tw-mr-1"
                        />
                      </Tooltip>
                    ) : null}
                    <InsertTextVariables
                      handleOpenMenu={handleOpenVariableMenu}
                      handleCloseMenu={handleCloseVariableMenu}
                      variableMenuOpen={variableMenuOpen}
                      type="for-tasks"
                      handleSelectVariable={(v) => {
                        setFieldValue("name", handleAddVariable(v));
                      }}
                    />
                  </>
                }
                id="elementNameInput"
                value={field.value}
                size="biggest"
                mode="flat"
                tabIndex={0}
                maxLength={150}
                placeholder={`${placeHolderText} *`}
                data-testid="project-template-element-name-input"
                className="tw-w-full tw-p-0"
                onSelect={onSelectionChange}
                onKeyDown={handleKeyPress}
                onChange={(e) => {
                  form.setFieldTouched(field.name, false);
                  form.setFieldError(field.name, undefined);
                  form.setFieldValue(field.name, e.target.value);
                }}
                autoFocus
              />
            </div>
          )}
        </Field>
        <div
          className={classNames(
            "tw-border-border-primary tw-border-solid tw-border tw-border-t-transparent task_form_description_container",
            {
              "tw-flex-1": elementType !== PROJECT_TEMPLATE_RECURRING_TASK,
            }
          )}
        >
          <CustomFormField name="body">
            {({ field }) => (
              <VisualEditorContainer
                projectId={values.id}
                parentType={elementType}
                parentId={values?.task_list_id}
                content={field.value}
                bodyMode={values?.body_mode}
                onKeyDown={handleKeyPress}
                onChange={field.onChange}
                placeholder={t(`Write a description or press "/" for commands`)}
                showTextVariables
              />
            )}
          </CustomFormField>
        </div>
        <div className="tw-px-6 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent">
          <CustomFormField name="attachments">
            {({ field }) => (
              <SelectAttachments
                {...fileHandlerRest}
                uploading={uploading}
                attachmentIds={List(values.attachmentIds)}
                filesToAttach={field.value.filesToAttach}
                filesToDrop={field.value.filesToDrop}
                onChange={field.onChange}
                onOpenFileWindow={handleOpenFileWindow}
              />
            )}
          </CustomFormField>
        </div>
        <div
          className={classNames(
            "tw-px-6 tw-py-5 tw-h-[66px] tw-flex tw-items-center tw-border tw-border-border-primary tw-border-solid tw-border-t-transparent",
            { "tw-rounded-b-md": !isDiscussionOrNote }
          )}
        >
          <Body2
            weight="bold"
            className="tw-truncate tw-w-[100px] tw-mr-1"
          >
            {t("Subscribers")}
          </Body2>
          <CustomFormField name="subscribers">
            {({ field }) => {
              return (
                <SubscribersPicker
                  onChange={field.onChange}
                  userIds={subscriberIds}
                  hideClients={values?.is_hidden_from_clients}
                  selected={field.value}
                  target={<SubscribersAvatarList subscriberIds={field.value} />}
                />
              );
            }}
          </CustomFormField>
        </div>
      </div>
      {isDiscussionOrNote ? (
        <div className="tw-px-7 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent tw-rounded-b-md tw-shrink-0">
          <CustomFormField name="is_hidden_from_clients">
            {({ field }) => (
              <FormCheckbox
                id="discussion_form_hidden_from_clients"
                checked={field.value}
                onChange={field.onChange}
                className="form_field tw-mb-2"
                text={t("Hidden from clients")}
              />
            )}
          </CustomFormField>

          <CustomFormField name="is_pinned">
            {({ field }) => (
              <FormCheckbox
                id="discussion_form_is_pinned"
                checked={field.value}
                onChange={field.onChange}
                className="form_field"
                text={t("Pinned to top")}
              />
            )}
          </CustomFormField>
        </div>
      ) : null}

      <AngDropFiles onDrop={handleUpload} />
    </>
  );
};

TemplateElementContent.propTypes = {
  fieldRef: PropTypes.object,
  fileInputRef: PropTypes.object,
  additionalFields: PropTypes.node,
};

export default TemplateElementContent;
